import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { auth, type LoginResponse } from '@/shared/auth/storage';
import { publicRoutes, buildScopedPath } from '@/app/routes';
import { useToast } from '@/app/providers/ToastProvider';

type Mode = 'otp' | 'password';

export function LoginPage() {
  const nav = useNavigate();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('otp');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [otpInfo, setOtpInfo] = useState<{ sessionId: string; instruction?: string } | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  function afterLogin(res: LoginResponse) {
    auth.saveLogin(res);
    if (res.DefaultTenantId && res.DefaultCompanyId) {
      nav(buildScopedPath('dashboard', { tenantId: res.DefaultTenantId, companyId: res.DefaultCompanyId }));
    } else {
      nav(publicRoutes.selectScope);
    }
  }

  async function startOtp() {
    if (!phone.trim()) return toast('Утасны дугаар оруулна уу.', 'warning');
    setBusy(true);
    try {
      const r = await authApi.startOtp(phone.trim());
      setOtpInfo({ sessionId: r.SessionId, instruction: r.DisplayInstruction });
      pollRef.current = window.setInterval(() => poll(r.SessionId), 2500);
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function poll(sessionId: string) {
    try {
      const r = await authApi.pollOtp(sessionId);
      if (r.SessionStatus === 'VERIFIED') {
        if (pollRef.current) window.clearInterval(pollRef.current);
        const res = await authApi.phoneLogin(phone.trim(), sessionId);
        afterLogin(res);
      } else if (r.SessionStatus === 'EXPIRED' || r.SessionStatus === 'FAILED') {
        if (pollRef.current) window.clearInterval(pollRef.current);
        setOtpInfo(null);
        toast('Баталгаажуулалт амжилтгүй. Дахин оролдоно уу.', 'danger');
      }
    } catch {
      /* keep polling */
    }
  }

  async function doPassword() {
    if (!phone.trim() || !password) return toast('Утас, нууц үг оруулна уу.', 'warning');
    setBusy(true);
    try {
      afterLogin(await authApi.passwordLogin(phone.trim(), password));
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cg-auth-wrap">
      <div className="ts-card cg-auth-card">
        <div className="cg-brand" style={{ padding: '0 0 18px' }}>
          <i className="bi bi-truck-front" /> TseTsu Карго
        </div>
        <div className="ts-flex-row" style={{ marginBottom: 18, gap: 8 }}>
          <button className={`ts-btn ${mode === 'otp' ? 'ts-btn-primary' : 'ts-btn-ghost'}`} onClick={() => setMode('otp')}>
            SMS код
          </button>
          <button
            className={`ts-btn ${mode === 'password' ? 'ts-btn-primary' : 'ts-btn-ghost'}`}
            onClick={() => setMode('password')}
          >
            Нууц үг
          </button>
        </div>

        <label className="ts-label">Утасны дугаар</label>
        <input
          className="ts-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="99112233"
          inputMode="tel"
          style={{ marginBottom: 14 }}
        />

        {mode === 'password' && (
          <>
            <label className="ts-label">Нууц үг</label>
            <input
              className="ts-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doPassword()}
              style={{ marginBottom: 14 }}
            />
            <button className="ts-btn ts-btn-primary" style={{ width: '100%' }} disabled={busy} onClick={doPassword}>
              Нэвтрэх
            </button>
          </>
        )}

        {mode === 'otp' &&
          (otpInfo ? (
            <div style={{ textAlign: 'center' }}>
              <div className="ts-page-sub" style={{ marginBottom: 8 }}>
                {otpInfo.instruction || 'Илгээсэн зааврын дагуу SMS илгээж баталгаажуулна уу.'}
              </div>
              <i className="bi bi-arrow-repeat" style={{ fontSize: '1.6rem', color: 'var(--ts-brand)' }} />
              <div className="ts-page-sub">Баталгаажуулалтыг хүлээж байна…</div>
            </div>
          ) : (
            <button className="ts-btn ts-btn-primary" style={{ width: '100%' }} disabled={busy} onClick={startOtp}>
              Код авах
            </button>
          ))}
      </div>
    </div>
  );
}
